import { Collection, getFirestore } from 'firestorter';
import format from 'string-template';

import { firestorePaths, pathPlaceholder } from '../../utils/paths';
import BaseDocument from '../../Model/BaseDocument';
import CollectionName from '../../utils/collections';

/**
 * Class representing an Announcement Comment.
 * A comment is a post that is shared to an announcement.
 *
 * @class AnnouncementComment
 * @extends BaseDocument
 */
class AnnouncementComment extends BaseDocument {
  constructor(id, announcementId, opts) {
    super(
      `${format(firestorePaths.ANNOUNCEMENT_COMMENT, { [pathPlaceholder.ANNOUNCEMENT_ID]: announcementId })}/${id}`,
      opts,
    );
  }

  /**
   * The name of the user who commented.
   * @returns {string}
   */
  get userName() {
    return this.data.userName;
  }

  /**
   * The comment.
   * @returns {string}
   */
  get comment() {
    return this.data.comment;
  }

  /**
   * The date the comment was added.
   * @returns {Date}
   */
  get createdAt() {
    return this.data.createdAt;
  }

  /**
   * The URL of the user's avatar.
   * @returns {string}
   */
  get userAvatarUrl() {
    return this.data.userAvatarUrl;
  }

  /**
   * The ID of the user who commented.
   * @returns {string}
   */
  get userId() {
    return this.data.userId;
  }

  /**
   * Whether the comment is removed.
   * @returns {boolean}
   */
  get isRemoved() {
    return this.data.isRemoved;
  }

  /**
   * The number of reactions to the comment.
   * @returns {number}
   */
  get reactions() {
    return this.data.reactions || 0;
  }

  /**
   * The ID of the announcement that the comment belongs to.
   * @returns {string}
   */
  get announcementId() {
    return this.data.announcementId;
  }

  /**
   * The pod id associated with the comment.
   * @returns {string}
   */
  get podId() {
    return this.data.podId || '';
  }

  /**
   * The date the comment was updated.
   * @returns {Date}
   */
  get updatedAt() {
    return this.data.updatedAt;
  }

  /**
   * Whether the comment is by the coach.
   * @returns {boolean}
   */
  get isByCoach() {
    return this.data.isByCoach;
  }

  /**
   * The latest date the comment was created or updated.
   * @returns {Date}
   */
  get displayDate() {
    return this.updatedAt || this.createdAt;
  }

  /**
   * The attachments (photos, videos, files) attached to the comment.
   * @returns {Object[]}
   */
  get attachments() {
    return this.data.attachments || [];
  }

  /**
   * Updates the comment.
   * @param {Object} commentData - The data of the comment.
   * @returns {Promise<void>}
   */
  async updateComment(commentData) {
    return this.updateFields(commentData);
  }

  /**
   * Removes the announcement comment.
   * @param {string} userId - The ID of the user who removed the comment.
   * @returns {Promise<void>}
   */
  async remove(userId) {
    return this.updateFields({
      isRemoved: true,
      removedBy: userId,
      removedAt: new Date(),
    });
  }

  /**
   * Marks the comment as read by the coach.
   * @returns {Promise<void>}
   */
  async markAsRead() {
    return this.updateFields({
      readByCoach: true,
    });
  }

  /**
   * Get the comment by its ID.
   * @param {string} commentId - The ID of the comment.
   * @param {string} announcementId - The ID of the announcement.
   * @returns {Promise<AnnouncementComment>}
   */
  static async getCommentById(commentId, announcementId) {
    const commentRef = new AnnouncementComment(commentId, announcementId);
    await commentRef.init();
    return commentRef;
  }

  /**
   * Get the comments for an announcement.
   * @param {string} announcementId - The ID of the announcement.
   * @returns {Collection}
   */
  static async getAnnouncementComments(announcementId) {
    const commentsCol = new Collection(
      format(firestorePaths.ANNOUNCEMENT_COMMENT, { [pathPlaceholder.ANNOUNCEMENT_ID]: announcementId }),
      {
        createDocument: ({ id }, opts) => new AnnouncementComment(
          id,
          announcementId,
          { ...opts, disableObserverRefCount: true },
        ),
        query: (ref) => ref
          .where('isRemoved', '==', false)
          .orderBy('createdAt', 'desc'),
      },
    );
    await commentsCol.fetch();
    return commentsCol;
  }

  /**
   * Get the unread comments of a coach.
   * @param {string} coachId - The ID of the coach.
   * @returns {Promise<Collection>}
   */
  static async getUnreadCommentsOfCoach(coachId) {
    const db = getFirestore();
    const unreadCommentsCollection = new Collection(() => db.collectionGroup(CollectionName.ANNOUNCEMENT_COMMENT), {
      createDocument: ({ id }, opts) => {
        // Extract announcementId from the document data
        const docRef = opts?.snapshot?.ref;
        const announcementId = docRef?.parent?.parent?.id;
        return new AnnouncementComment(id, announcementId, opts);
      },
      query: (ref) => ref
        .where('readByCoach', '==', false)
        .where('isByCoach', '==', false)
        .where('isRemoved', '==', false)
        .where('coachId', '==', coachId),
    });
    // Fetch initial data
    await unreadCommentsCollection.fetch();
    return unreadCommentsCollection;
  }

  /**
   * Adds a comment to an announcement.
   * @param {string} announcementId - The ID of the announcement.
   * @param {Object} commentData - The data of the comment.
   * @returns {Promise<string>} The id of the created firestore document
   */
  static async addComment(announcementId, commentData) {
    const commentsCol = new Collection(
      format(firestorePaths.ANNOUNCEMENT_COMMENT, { [pathPlaceholder.ANNOUNCEMENT_ID]: announcementId }),
    );
    const { id } = await commentsCol.add(commentData);
    return id;
  }

  /**
   * Get the comments for given IDs.
   * @param {Array<{commentId: string, announcementId: string}>} commentIds - The IDs of the comments.
   * @returns {Promise<Array<AnnouncementComment>>} The comments.
   */
  static async getCommentsForGivenIds(commentIds) {
    const commentPromises = commentIds.map((comment) => this.getCommentById(comment.commentId, comment.announcementId));
    const comments = await Promise.all(commentPromises);
    return comments;
  }
}

export default AnnouncementComment;
